import { useEffect, useState } from "react";

import { extractSignedXdr } from "@/pages/MultisigSetup/components/CoSignerKeyStep";

import { useMultisigUnenrollmentSubmitSignature } from "@/apiQueries/multisigUnenrollment";

import { getUnenrollmentErrorMessage, UnenrollmentError } from "@/helpers/unenrollmentErrors";

import type { UnenrollmentSignStage } from "../components/UnenrollmentSignModal";
import type { SessionTypes } from "@via/walletconnect-types";

import type { MultisigUnenrollmentStatus } from "@/types";

import {
  DEFAULT_CONNECT_PARAMS,
  STELLAR_MAINNET_CHAIN_ID,
  STELLAR_METHOD_SIGN_XDR,
} from "@/services/walletConnect/constants";
import { WcSignClient } from "@/services/walletConnect/wcSignClient";

// The key the wallet exposes for the chain the request is sent on; account
// order in the session is not a chain guarantee.
const signingPublicKey = (session: SessionTypes.Struct): string | null =>
  session.namespaces?.stellar?.accounts
    ?.find((account) => account.startsWith(`${STELLAR_MAINNET_CHAIN_ID}:`))
    ?.split(":")
    .pop() || null;

const errorMessage = (err: unknown): string =>
  err instanceof UnenrollmentError
    ? getUnenrollmentErrorMessage(err)
    : err instanceof Error && err.message
      ? err.message
      : "VIA Wallet couldn't sign the request. Try again.";

/**
 * Pairs VIA Wallet over WalletConnect, asks it to sign the unenrollment XDR
 * (`stellar_signXDR`, same request as enrollment) and submits the signature.
 * Pairing starts on mount and the session is dropped on unmount; a failure
 * shows the error and starts a fresh pairing so the Owner can scan again.
 */
export const useUnenrollmentSign = ({
  userId,
  txId,
  unsignedXdr,
}: {
  userId: string;
  txId: string;
  unsignedXdr: string;
}) => {
  const { mutateAsync: submitSignature } = useMultisigUnenrollmentSubmitSignature();
  const [stage, setStage] = useState<UnenrollmentSignStage>("pairing");
  const [uri, setUri] = useState<string | null>(null);
  const [signerPublicKey, setSignerPublicKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** What the backend returned for the submitted signature; the page feeds it to `lastKnownTx`. */
  const [result, setResult] = useState<MultisigUnenrollmentStatus | null>(null);

  useEffect(() => {
    let isActive = true;
    let client: WcSignClient | null = null;

    const fail = (err: unknown) => {
      if (!isActive) return;
      setError(errorMessage(err));
      void client?.disconnect().catch(() => undefined);
      void pair();
    };

    const requestSignature = () =>
      new Promise<string>((resolve, reject) => {
        if (!client) {
          reject(new Error("VIA Wallet is not connected."));
          return;
        }
        client
          .sendRequest(
            STELLAR_MAINNET_CHAIN_ID,
            { method: STELLAR_METHOD_SIGN_XDR, params: [{ xdr: unsignedXdr }] },
            (_p, response) => {
              const signed = extractSignedXdr(response);
              if (signed) resolve(signed);
              else
                reject(
                  new Error("VIA Wallet returned an unexpected response for stellar_signXDR."),
                );
            },
            (_p, err) => reject(err),
          )
          .catch(reject);
      });

    const sign = async (session: SessionTypes.Struct) => {
      const publicKey = signingPublicKey(session);
      if (!publicKey) {
        fail(
          new Error("VIA Wallet approved the session but did not return a Stellar pubnet account."),
        );
        return;
      }
      setError(null);
      setStage("signing");
      try {
        const signedXdr = await requestSignature();
        if (!isActive) return;
        const submitted = await submitSignature({ txId, signerPublicKey: publicKey, signedXdr });
        if (!isActive) return;
        setSignerPublicKey(publicKey);
        setResult(submitted);
        setStage("signed");
      } catch (err) {
        fail(err);
      }
    };

    const pair = async () => {
      if (!client || !isActive) return;
      setUri(null);
      setStage("pairing");
      try {
        const nextUri = await client.connect(
          { ...DEFAULT_CONNECT_PARAMS },
          true,
          (_p, session) => {
            if (isActive) void sign(session);
          },
          (_p, err) => fail(err),
        );
        if (!isActive) return;
        if (nextUri) {
          setUri(nextUri);
          setStage("scan");
        }
      } catch (err) {
        if (isActive) setError(errorMessage(err));
      }
    };

    void (async () => {
      try {
        const initialized = await WcSignClient.init(userId);
        if (!isActive) {
          await initialized.disconnect().catch(() => undefined);
          return;
        }
        client = initialized;
        const restored = client.getSession();
        if (restored) await sign(restored);
        else await pair();
      } catch (err) {
        if (isActive) setError(errorMessage(err));
      }
    })();

    return () => {
      isActive = false;
      void client?.disconnect().catch(() => undefined);
    };
  }, [userId, txId, unsignedXdr, submitSignature]);

  return { stage, uri, signerPublicKey, error, result };
};
